import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { clientApi, getClientToken, CLIENT_API } from "../../lib/clientApi";
import { Save, Send, FileText, ArrowLeft, CheckCircle2, Clock, AlertCircle, Download } from "lucide-react";

const STATUS_LABELS = {
  da_compilare: { label: "Da compilare", color: "bg-[#FEF3C7] text-[#B45309]", Icon: Clock },
  bozza: { label: "Bozza salvata", color: "bg-[#DBEAFE] text-[#1E40AF]", Icon: Save },
  compilato: { label: "Compilato", color: "bg-[#EAF8F0] text-[#007A3D]", Icon: CheckCircle2 },
  approvato: { label: "Approvato", color: "bg-[#D1FAE5] text-[#065F46]", Icon: CheckCircle2 },
  da_correggere: { label: "Da correggere", color: "bg-[#FEE2E2] text-[#B91C1C]", Icon: AlertCircle },
};

function Field({ f, value, onChange, disabled }) {
  const common = "w-full rounded-xl border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#00A651] disabled:bg-[#F9FAFB] disabled:text-[#6B7280]";
  const props = { disabled, "data-testid": `field-${f.key}` };
  switch (f.type) {
    case "textarea":
      return <textarea {...props} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} placeholder={f.placeholder} />;
    case "number":
      return <input {...props} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={common} placeholder={f.placeholder} />;
    case "importo":
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6B7280]">€</span>
          <input {...props} type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={`${common} pl-7`} placeholder={f.placeholder} />
        </div>
      );
    case "date":
      return <input {...props} type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} />;
    case "checkbox":
      return (
        <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] py-1">
          <input {...props} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#00A651]" />
          {f.placeholder || "Confermo"}
        </label>
      );
    case "radio":
      return (
        <div className="flex flex-col gap-1.5">
          {(f.options || []).map((o) => (
            <label key={o} className="inline-flex items-center gap-2 text-sm">
              <input type="radio" disabled={disabled} name={f.key} value={o} checked={value === o} onChange={() => onChange(o)} className="w-4 h-4 accent-[#00A651]" />
              {o}
            </label>
          ))}
        </div>
      );
    case "select":
      return (
        <select {...props} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${common} appearance-none bg-white`}>
          <option value="">Seleziona…</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    default:
      return <input {...props} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} placeholder={f.placeholder} />;
  }
}

export default function ClientFormFiller() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!getClientToken()) { nav("/area-riservata", { replace: true }); return; }
    clientApi.get(`/client/fillable-forms/${id}`)
      .then((r) => { setForm(r.data); setValues(r.data.values || {}); })
      .catch(() => nav("/area-riservata/dashboard"));
  }, [id, nav]);

  // Fetch PDF template
  useEffect(() => {
    if (!form?.has_template_pdf) return;
    const token = getClientToken();
    fetch(`${CLIENT_API}/client/fillable-forms/${id}/pdf-template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : null)
      .then((blob) => { if (blob) setPdfUrl(URL.createObjectURL(blob)); })
      .catch(() => {});
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
    // eslint-disable-next-line
  }, [form, id]);

  const isLocked = form && (form.status === "compilato" || form.status === "approvato");

  const doSave = async (silent = false) => {
    if (!form || isLocked) return;
    if (!silent) setSaving(true);
    try {
      await clientApi.put(`/client/fillable-forms/${id}/save`, { values });
      setLastSaved(new Date());
      setDirty(false);
      setForm((f) => ({ ...f, status: "bozza" }));
    } catch { /* ignore */ }
    finally { if (!silent) setSaving(false); }
  };

  // Auto-save on values change (debounced)
  useEffect(() => {
    if (!form || isLocked || !dirty) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { doSave(true); }, 3000);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line
  }, [values, dirty]);

  const setValue = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setDirty(true); };

  const submit = async () => {
    if (!confirm("Inviare il documento? Dopo l'invio non potrai più modificarlo (a meno che lo studio non lo riapra).")) return;
    setSubmitting(true);
    try {
      const { data } = await clientApi.post(`/client/fillable-forms/${id}/submit`, { values });
      setForm((f) => ({ ...f, status: data.status, submitted_at: new Date().toISOString(), has_submitted_pdf: true }));
      setDirty(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore invio");
    } finally { setSubmitting(false); }
  };

  const downloadSubmitted = async () => {
    const token = getClientToken();
    const res = await fetch(`${CLIENT_API}/client/fillable-forms/${id}/pdf-submitted`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${form.title}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const missingRequired = useMemo(() => {
    if (!form) return [];
    return form.fields.filter((f) => {
      if (!f.required) return false;
      const v = values[f.key];
      if (f.type === "checkbox") return !v;
      return v === undefined || v === null || (typeof v === "string" && !v.trim());
    });
  }, [form, values]);

  if (!form) return <div className="h-[60vh] flex items-center justify-center text-[#6B7280]">Caricamento…</div>;
  const st = STATUS_LABELS[form.status] || STATUS_LABELS.da_compilare;

  return (
    <div data-testid={`form-filler-${id}`} className="min-h-[80vh] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/area-riservata/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#007A3D] mb-6">
          <ArrowLeft className="w-4 h-4" /> Torna alla bacheca
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${st.color}`}>
                <st.Icon className="w-3 h-3" /> {st.label}
              </span>
            </div>
            <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#1A1A1A]">{form.title}</h1>
            {form.description && <p className="mt-3 text-sm text-[#6B7280] max-w-2xl">{form.description}</p>}
          </div>
          {form.has_submitted_pdf && (
            <button data-testid="download-submitted" onClick={downloadSubmitted} className="inline-flex items-center gap-1.5 bg-[#007A3D] hover:bg-[#00A651] text-white rounded-full px-5 py-2.5 text-sm font-medium">
              <Download className="w-4 h-4" /> Scarica compilato
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PDF template */}
          {form.has_template_pdf && (
            <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm overflow-hidden lg:sticky lg:top-4 h-fit">
              <div className="p-4 border-b border-[#E5E7EB] flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
                <FileText className="w-4 h-4 text-[#007A3D]" /> Modello di riferimento
              </div>
              {pdfUrl ? (
                <object data={pdfUrl} type="application/pdf" className="w-full" style={{ height: "70vh" }}>
                  <div className="p-6 text-center text-sm text-[#6B7280]">Il PDF non può essere visualizzato in linea. <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-[#007A3D] underline">Apri in nuova scheda</a></div>
                </object>
              ) : (
                <div className="p-8 text-center text-sm text-[#6B7280]">Caricamento anteprima…</div>
              )}
            </div>
          )}

          {/* Form */}
          <div className={form.has_template_pdf ? "" : "lg:col-span-2 lg:max-w-3xl lg:mx-auto lg:w-full"}>
            <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm p-6 md:p-8 space-y-5">
              {isLocked && (
                <div className="rounded-xl bg-[#EAF8F0] border border-[#A7E2C0] p-4 text-sm text-[#065F46]">
                  Documento inviato — non è più modificabile. Se serve, contatta lo studio.
                </div>
              )}
              {form.fields.length === 0 && <div className="text-sm text-[#6B7280]">Questo modulo non ha ancora campi definiti.</div>}
              {form.fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1A1A1A] mb-2">
                    {f.label} {f.required && <span className="text-[#B91C1C]">*</span>}
                  </label>
                  <Field f={f} value={values[f.key]} onChange={(v) => setValue(f.key, v)} disabled={isLocked} />
                  {f.help && <p className="text-xs text-[#6B7280] mt-1">{f.help}</p>}
                </div>
              ))}

              {!isLocked && form.fields.length > 0 && (
                <div className="pt-4 border-t border-[#F3F4F6]">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div className="text-xs text-[#6B7280]">
                      {saving ? "Salvataggio…" : lastSaved ? `Ultimo salvataggio: ${lastSaved.toLocaleTimeString("it-IT")}` : "Auto-salvataggio attivo"}
                    </div>
                    {missingRequired.length > 0 && (
                      <div className="text-xs text-[#B91C1C]">Mancano {missingRequired.length} campi obbligatori</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="save-draft" onClick={() => doSave(false)} disabled={saving || !dirty} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#E5E7EB] text-sm font-medium text-[#1A1A1A] hover:border-[#00A651] hover:text-[#007A3D] disabled:opacity-50">
                      <Save className="w-4 h-4" /> Salva bozza
                    </button>
                    <button data-testid="submit-form" onClick={submit} disabled={submitting || missingRequired.length > 0} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#00A651]/20">
                      <Send className="w-4 h-4" /> Invia documento completato
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
